/**
 * The signed-in shell from the design (dashboard-v7): wordmark + profile initial (menu lists every page), the
 * "Private & confidential" strip, then the page. Gallery/Vault are dark "rooms" (no header); Text is full-screen.
 */
import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { dashSprite } from '~/design/sprites'
import { FEATURES } from '~/config/mode'
import { Icon, Sprite, ToastHost, displayName } from './ui'
import { PinProvider, usePin } from './pin'
import { useUnread } from './useUnread'
import { signOutEverywhere } from './session'
import { nick } from './device'
import { SUPPORT_EMAIL } from '@/config/brandConfig'

const ROOMS = ['/gallery']

export function DashLayout() {
  const { pathname } = useLocation()
  const room = ROOMS.some((r) => pathname.startsWith(r))
  const chatOpen = pathname.startsWith('/text')

  // The design changes the phone status-bar colour in the dark rooms
  useEffect(() => {
    const m = document.querySelector('meta[name="theme-color"]')
    if (m) m.setAttribute('content', room ? '#151514' : '#F3F2E7')
    window.scrollTo(0, 0)
  }, [room, pathname])

  return (
    <div className={`cw-dash${room ? ' room' : ''}${chatOpen ? ' chat-open' : ''}`}>
      <Sprite svg={dashSprite} />
      <ToastHost>
        <PinProvider>
          <a className="skip" href="#main">Skip to content</a>
          {/* owner 11-04: no site header behind the full-screen chat (it showed through + disturbed scrolling on phones) */}
          {!chatOpen && <Header />}
          <Outlet />
        </PinProvider>
      </ToastHost>
    </div>
  )
}

function Header() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { go } = usePin()
  const { unread } = useUnread()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const name = displayName(user)
  const [nickV, setNickV] = useState(() => (FEATURES.nickname ? nick.get(user?.id) : ''))
  useEffect(() => {
    const on = () => setNickV(FEATURES.nickname ? nick.get(user?.id) : '')
    on(); window.addEventListener('cw-nick-changed', on)
    return () => window.removeEventListener('cw-nick-changed', on)
  }, [user?.id])
  const initial = ((nickV || name || 'C').trim()[0] || 'C').toUpperCase()
  // the fan's own profile photo (profiles.avatar_url, the same one today's site shows); Settings → Upload Photo updates it
  const [photo, setPhoto] = useState<string | null>((user as any)?.avatar_url || null)
  useEffect(() => {
    setPhoto((user as any)?.avatar_url || null)
    const on = (e: Event) => setPhoto((e as CustomEvent<string | null>).detail ?? null)
    window.addEventListener('cw-avatar-changed', on)
    return () => window.removeEventListener('cw-avatar-changed', on)
  }, [user])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('click', onDoc); document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('click', onDoc); document.removeEventListener('keydown', onKey) }
  }, [])
  useEffect(() => setOpen(false), [pathname])

  const item = (path: string, icon: string, label: string, opts: { locked?: boolean; dot?: boolean } = {}) => (
    <button type="button" aria-current={pathname.startsWith(path) ? 'page' : undefined}
      onClick={() => { setOpen(false); if (opts.locked) go(path); else navigate(path) }}>
      <Icon id={icon} className="icon menu-ico" />{label}
      {opts.dot && <><span className="unread-dot menu-dot" aria-hidden="true" /><span className="sr-only">, {unread} unread</span></>}
    </button>
  )

  return (
    <>
      <header className="site-header">
        <div className="wrap">
          <a className="wordmark" href="/home" onClick={(e) => { e.preventDefault(); navigate('/home') }} aria-label="caryn’s world, home">
            caryn’s world <Icon id="i-heart" />
          </a>
          <div className="header-tools">
            <div className="account" ref={box}>
              <button className="profile-btn" type="button" aria-haspopup="true" aria-expanded={open} aria-label="Profile menu" onClick={() => setOpen((o) => !o)}>
                {photo ? <img className="v2-avatar-img" src={photo} alt="" onError={() => setPhoto(null)} /> : <span>{initial}</span>}
              </button>
              <div className="account-menu" hidden={!open}>
                <p className="menu-name">{name}{user?.email && <small className="v2-menu-email">Logged in as {user.email}</small>}</p>
                {item('/home', 'i-home', 'Home')}
                {item('/gallery', 'i-image', 'Gallery')}
                {item('/text', 'i-chat', 'Text', { locked: true, dot: unread > 0 })}
                {item('/inbox', 'i-mail', 'Email')}
                {FEATURES.diary && item('/diary', 'i-journal', 'Diary')}
                {item('/vault', 'i-lock', 'Your Vault', { locked: true })}
                <hr />
                {item('/settings', 'i-gear', 'Settings')}
                <a className="v2-menu-support" href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
                <button type="button" onClick={() => signOutEverywhere(navigate)}>Sign out</button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="secure-strip">
        <div className="wrap">
          <Icon id="i-lock" />
          <span><strong>Private &amp; confidential</strong><span className="sep"> · </span>Secure session</span>
        </div>
      </div>
    </>
  )
}
